import { Driver } from '@aaa-ncnu-ie/mypolicy-api-policies';
import { Box, Badge, Stack, SxProps, Theme, Typography } from '@mui/material';
import React from 'react';

import ChevronDownIcon from '../../../../assets/icons/ChevronDown';
import ChevronUpIcon from '../../../../assets/icons/ChevronUp';
import ExpandableList from '../../../../components/ExpandableList';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import MySafeDriveBadge from './components/MySafeDriveBadge';
import MySafeDriveCoveragesCard from './components/MySafeDriveCoverageCard';
import SMARTtrekBadge from './components/SMARTtrekBadge';
import SMARTtrekCoveragesCard from './components/SMARTtrekCoverageCard';

interface Props {
  data: Driver;
  policyNumber?: string;
}

const DriverDetails: React.FC<Props> = ({
  data: {
    coverages,
    firstName,
    lastName,
    type,
    dateOfBirth,
    ubiEnrolled,
    safeDriveEnrolled,
    smartTrekEnrolled,
  },
  policyNumber,
}: Props) => {
  const { track } = useAnalytics();
  const buttonDisabled =
    !(coverages.length > 0) &&
    !(ubiEnrolled || safeDriveEnrolled || smartTrekEnrolled);
  const content = (isExpanded: boolean) => {
    const getChevron = () =>
      isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />;

    return (
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          p: 2,
          width: 1
        }}>
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            className="csaa-qm-encrypt"
            sx={{
              fontWeight: 700,
              fontSize: { mobile: 16, tablet: 18 },
              lineHeight: '24px',
              color: colors.csaaBlack,
              letterSpacing: { mobile: 0.3, tablet: 0.4 },
            }}
          >
            {firstName} {lastName}
          </Typography>
          {type === 'Rated Driver' && (
            <Typography
              className="csaa-qm-encrypt"
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
                color: colors.csaaMid,
              }}
            >
              Date of birth: {dateOfBirth}
            </Typography>
          )}
          {(ubiEnrolled || smartTrekEnrolled) && (
            <SMARTtrekBadge showMobile sx={{ mt: 1 }} />
          )}
          {safeDriveEnrolled && <MySafeDriveBadge showMobile sx={{ mt: 1 }} />}
          <DriverType driverType={type} showMobile sx={{ mt: 1 }} />
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            height: "fit-content"
          }}>
          {(ubiEnrolled || smartTrekEnrolled) && (
            <SMARTtrekBadge showMobile={false} />
          )}
          {safeDriveEnrolled && <MySafeDriveBadge showMobile={false} />}
          <DriverType driverType={type} showMobile={false} />
          {!buttonDisabled && (
            <Stack
              sx={{
                background: 'rgba(11, 20, 33, 0.1)',
                cursor: 'pointer',
                p: 0.5,
                borderRadius: 1,
                height: 'fit-content',
              }}
            >
              {getChevron()}
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  const DriverType = ({
    showMobile,
    driverType,
    sx,
  }: {
    showMobile: boolean;
    driverType: string;
    sx?: SxProps<Theme>;
  }) => (
    <Badge
      sx={{
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        borderRadius: 1,
        background: colors.csaaNeutralBackground,
        py: 0.5,
        px: 1,
        fontWeight: 500,
        lineHeight: '16px',
        fontSize: 14,
        letterSpacing: 0.2,
        height: 'fit-content',
        width: 'fit-content',
        color: colors.csaaDark,
        display: {
          mobile: showMobile ? 'block' : 'none',
          tablet: showMobile ? 'none' : 'block',
        },
        ...sx,
      }}
    >
      {driverType}
    </Badge>
  );

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: colors.csaaBorder,
        borderRadius: 2,
      }}
    >
      <ExpandableList
        disabled={buttonDisabled}
        content={content}
        id={`driver-coverage-${firstName}-${dateOfBirth}`}
        onClick={(isOpen) =>
          track('Driver Coverage Details Toggled', {
            event_type: 'UX Modified',
            location: 'Feature Card',
            link: `${firstName} ${lastName}`,
            selection: isOpen ? 'Closed' : 'Opened',
            policy_number: policyNumber,
          })
        }
      >
        <Stack
          spacing={1}
          sx={{
            p: 2,
            backgroundColor: colors.csaaNeutralBackground,
            borderTop: '1px solid',
            borderColor: colors.csaaBorder,
            borderBottomRightRadius: 8,
            borderBottomLeftRadius: 8,
          }}
        >
          {coverages.map((item, idx) => (
            <Box
              key={`${item.coverageDescription}-${idx}`}
              sx={{
                backgroundColor: 'white',
                p: { mobile: 2, tablet: 3 },
                boxShadow: `inset 0px 0px 0px 1px ${colors.csaaNeutral}`,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" sx={{
                justifyContent: "space-between"
              }}>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontSize: { mobile: 16, tablet: 18 },
                    lineHeight: '24px',
                    fontWeight: 700,
                    letterSpacing: { mobile: 0.3, tablet: 0.4 },
                  }}
                >
                  {item.coverageDescription}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontSize: { mobile: 16, tablet: 18 },
                    lineHeight: '24px',
                    fontWeight: 700,
                    letterSpacing: { mobile: 0.3, tablet: 0.4 },
                  }}
                >
                  {item.coverageLimit}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 400,
                  fontSize: { mobile: 14, tablet: 16 },
                  lineHeight: '24px',
                  color: colors.csaaMid,
                  mt: 1,
                  letterSpacing: { mobile: 0.2, tablet: 0.3 },
                }}
              >
                {item.coverageType}
              </Typography>
            </Box>
          ))}
          {(ubiEnrolled || smartTrekEnrolled) && (
            <SMARTtrekCoveragesCard policyNumber={policyNumber} />
          )}
          {safeDriveEnrolled && (
            <MySafeDriveCoveragesCard policyNumber={policyNumber} />
          )}
        </Stack>
      </ExpandableList>
    </Box>
  );
};

export default DriverDetails;
